import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import Button from '../../../GbsUI/UI/Button';
import ArrowLeftIcon from '../../../GbsUI/UI/icons/ArrowLeft';
import ArrowRightIcon from '../../../GbsUI/UI/icons/ArrowRight';
import Radio from '../../../GbsUI/UI/Radio';
import TextInput from '../../../GbsUI/UI/TextInput';
import SignupLayout from '../components/Layout';
import {
  DISCLOSE_OPTIONS_ALT,
  MILITARY_OPTIONS,
  SPORTS_OPTIONS,
} from '../constants';
import { SignupContext } from '../context';
import stepSevenSchema from '../schema/step-seven';

const SignupStepFive = ({ history }) => {
  const { state, setState } = useContext(SignupContext);
  const [formErrors, setFormErrors] = useState(new Map());
  const initialized = useRef(false);

  const validate = useCallback(
    (data, abortEarly = false, scrollToError = true) => {
      data = data || {};

      return stepSevenSchema
        .validate(data, { abortEarly })
        .then(data => {
          setFormErrors(new Map());
          return data;
        })
        .catch(errors => {
          if (errors.inner.length > 0) {
            const errorsMap = errors.inner.reduce((accum, item, index) => {
              const field = document.querySelector(`[name="${item.path}"]`);

              if (index === 0 && field && scrollToError) {
                field
                  .closest('.signup-form-item')
                  .scrollIntoView({ behavior: 'smooth' });
              }

              accum.set(item.path, item.errors);
              return accum;
            }, new Map());

            setFormErrors(errorsMap);
          } else {
            const errorsMap = new Map();
            errorsMap.set(errors.path, errors.errors);

            setFormErrors(errorsMap);
          }

          return false;
        });
    },
    []
  );

  const onSubmit = useCallback(
    e => {
      e.preventDefault();

      validate(state.leadership).then(valid => {
        if (valid) {
          setFormErrors(new Map());
          history.push('/signup/step/6');
          window.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        }
      });
    },
    [history, state.leadership, validate]
  );

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      return;
    }

    validate(state.leadership, true, false);
  }, [state, validate]);

  return (
    <form onSubmit={onSubmit}>
      <SignupLayout
        title={(
          <span className={'flex justify-between gap-2'}>
            <span>
              Step 5: Leadership & Extracurricular
            </span>
            <span className={'text-small'}>
              Step 5 of 8
            </span>
          </span>
        )}
        footer={
          <>
            <Button
              type={'button'}
              variant={'secondary'}
              size={'xl'}
              icon={ArrowLeftIcon}
              iconProps={{ size: 21 }}
              iconPosition={'left'}
              onClick={() => history.push('/signup/step/4')}
            >
              Back
            </Button>
            <Button
              size={'xl'}
              icon={ArrowRightIcon}
              iconProps={{ size: 21 }}
              iconPosition={'right'}
            >
              Continue
            </Button>
          </>
        }
      >
        <div className={'signup-form-row'}>
          <div className={'signup-form-item signup-form-item--full'}>
            <Radio
              label={'Did you participate in varsity sports during college?'}
              name={'athlete'}
              options={SPORTS_OPTIONS}
              checked={state?.leadership?.athlete}
              onChange={e => {
                setState(current => {
                  return {
                    ...current,
                    leadership: {
                      ...current.leadership,
                      athlete: e.target.value,
                      sports: '',
                    },
                  };
                });
              }}
            />
            {(state?.leadership?.athlete === 'Club' ||
              state?.leadership?.athlete === 'Varsity') && (
              <TextInput
                label={'List sports played and any additional comments'}
                name={'sports'}
                error={formErrors.get('sports')}
                value={state?.leadership?.sports}
                onChange={e => {
                  setState(current => {
                    return {
                      ...current,
                      leadership: {
                        ...current.leadership,
                        sports: e.target.value,
                      },
                    };
                  });
                }}
              />
            )}
          </div>
          <div className={'signup-form-item signup-form-item--full'}>
            <Radio
              label={
                'Have you served or are you currently serving in the military (U.S. or other)?'
              }
              name={'discloseMilitary'}
              options={DISCLOSE_OPTIONS_ALT}
              checked={state?.leadership?.discloseMilitary}
              onChange={e => {
                setState(current => {
                  return {
                    ...current,
                    leadership: {
                      ...current.leadership,
                      discloseMilitary: e.target.value,
                      military: '',
                      militaryNotes: '',
                    },
                  };
                });
              }}
            />
            {state?.leadership?.discloseMilitary === 'ok_to_say' && (
              <div className={'signup-form-item__sub-input'}>
                <Radio
                  name={'military'}
                  options={MILITARY_OPTIONS}
                  checked={state?.leadership?.military}
                  onChange={e => {
                    setState(current => {
                      return {
                        ...current,
                        leadership: {
                          ...current.leadership,
                          military: e.target.value,
                          militaryNotes: ['us_military', 'military'].includes(
                            e.target.value
                          )
                            ? current.leadership.militaryNotes
                            : '',
                        },
                      };
                    });
                  }}
                />
                {(state?.leadership?.military === 'us_military' ||
                  state?.leadership?.military === 'military') && (
                  <TextInput
                    label={'Military Branch / Additional Notes'}
                    name={'militaryNotes'}
                    value={state?.leadership?.militaryNotes}
                    error={formErrors.get('militaryNotes')}
                    onChange={e => {
                      setState(current => {
                        return {
                          ...current,
                          leadership: {
                            ...current.leadership,
                            militaryNotes: e.target.value,
                          },
                        };
                      });
                    }}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </SignupLayout>
    </form>
  );
};

export default SignupStepFive;
